import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { layoutSizes } from '@/theme/layoutSizes';
import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { Inline, Stack } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box, EmailIcon, Link, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { MouseEvent, PropsWithChildren, ReactElement, ReactNode } from 'react';

export type PublicFooterLink = {
  href: string;
  label: string;
};

export type PublicFooterLinkGroup = {
  title: string;
  links: readonly PublicFooterLink[];
};

type PublicShellProps = PropsWithChildren<{
  title?: ReactNode;
  actions?: ReactNode;
  footerLinkGroups?: readonly PublicFooterLinkGroup[];
  hideFooter?: boolean;
  pinFooterToViewport?: boolean;
}>;

type FooterLegalLink = {
  href: string;
  label: string;
  className?: string;
};

type FooterLink = FooterLegalLink | PublicFooterLink;

type FooterLinkGroup = {
  title: string;
  links: readonly FooterLink[];
};

const footerPolicyLinks: readonly FooterLegalLink[] = [
  { href: '/terms-of-service', label: 'Terms of Service' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/cookie-policy', label: 'Cookie Policy' },
];

const footerPreferenceLinks: readonly FooterLegalLink[] = [
  { href: '/acceptable-use-policy', label: 'Acceptable Use Policy' },
  { href: '/privacy-requests', label: 'Privacy Requests' },
  { href: '#', label: 'Consent Preferences', className: 'termly-display-preferences' },
];

export const PublicShell = ({
  children,
  title = 'FlowQA UI',
  actions,
  footerLinkGroups = [],
  hideFooter = false,
  pinFooterToViewport = true,
}: PublicShellProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const shellPaddingX = layoutMode === layoutModeTokens.wide ? s('lg') : s('md');
  const headerPaddingX = layoutMode === layoutModeTokens.wide ? s('2xl') : s('md');
  const breadcrumbPaddingX = layoutMode === layoutModeTokens.wide ? s('md') : s('sm');
  const footerLinkSx = {
    color: 'text.secondary',
    textDecorationColor: 'currentColor',
    whiteSpace: { lg: 'nowrap' },
    '&:hover': {
      color: 'text.secondary',
      textDecorationColor: 'currentColor',
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  } as const;
  const footerTextSx = {
    fontSize: (theme) => theme.typography.body1.fontSize,
    lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
  } satisfies SxProps<Theme>;
  const footerGroups = [
    ...footerLinkGroups,
    { title: 'Policies', links: footerPolicyLinks },
    { title: 'Preferences', links: footerPreferenceLinks },
  ] satisfies FooterLinkGroup[];
  const footerGridTemplateColumns = {
    xs: '1fr',
    md: 'minmax(0, 1fr) minmax(0, 2fr)',
    lg: 'minmax(0, 1.35fr) minmax(0, 2fr)',
  } as const;
  const openConsentPreferences = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    document.getElementById('termly-pref-trigger')?.click();
  };
  const renderFooterLink = (link: FooterLink): ReactElement => {
    const className = 'className' in link ? link.className : undefined;
    return (
      <Link
        component={link.href === '#' ? undefined : RouterLink}
        to={link.href === '#' ? undefined : link.href}
        href={link.href}
        className={className}
        onClick={className === 'termly-display-preferences' ? openConsentPreferences : undefined}
        underline="hover"
        sx={footerLinkSx}
      >
        <Text role="body" component="span" sx={footerTextSx}>
          {link.label}
        </Text>
      </Link>
    );
  };
  const [scrollReady, setScrollReady] = useState(false);
  const [footerReady, setFooterReady] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const resolveShellBackground = (theme: Theme): string => {
    return isLightTheme(theme)
      ? resolveSemanticColor(theme, 'surfacePaper')
      : resolveSemanticColor(theme, 'surfaceInset');
  };

  useEffect(() => {
    const id = window.setTimeout(() => {
      setScrollReady(true);
    }, 150);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  useLayoutEffect(() => {
    if (hideFooter) {
      setFooterReady(false);
      return;
    }

    const updateFooterReady = (): void => {
      const mainElement = mainRef.current;
      const contentElement = contentRef.current;
      if (mainElement === null || contentElement === null) return;
      const routeFallbackVisible = contentElement.querySelector('[data-public-route-fallback]');
      setFooterReady(routeFallbackVisible === null);
    };

    updateFooterReady();

    const ResizeObserverConstructor = (globalThis as { ResizeObserver?: typeof ResizeObserver })
      .ResizeObserver;
    const resizeObserver =
      ResizeObserverConstructor === undefined
        ? null
        : new ResizeObserverConstructor(updateFooterReady);
    if (mainRef.current !== null) {
      resizeObserver?.observe(mainRef.current);
    }
    if (contentRef.current !== null) {
      resizeObserver?.observe(contentRef.current);
    }

    const id = window.requestAnimationFrame(updateFooterReady);
    const mutationObserver = new MutationObserver(updateFooterReady);
    if (contentRef.current !== null) {
      mutationObserver.observe(contentRef.current, { childList: true, subtree: true });
    }
    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.cancelAnimationFrame(id);
    };
  }, [hideFooter, children]);

  return (
    <Box
      sx={(theme) => ({
        height: '100dvh',
        backgroundColor: resolveShellBackground(theme),
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        borderTop: 'none',
        overflow: 'hidden',
      })}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      <Box
        component="header"
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: resolveSemanticColor(theme, 'surfacePaper'),
          borderBottom: `1px solid ${resolveSemanticColor(theme, 'border')}`,
          minHeight: layoutSizes.topBar.height,
          paddingTop: s('xs'),
          paddingBottom: s('xs'),
          boxSizing: 'border-box',
          width: '100%',
          transform: 'translateZ(0)',
          willChange: 'transform',
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: s('md'),
            minHeight: layoutSizes.topBar.height,
            minWidth: 0,
            width: '100%',
            paddingLeft: headerPaddingX,
            paddingRight: layoutMode === layoutModeTokens.wide ? s('2xl') : s('sm'),
            boxSizing: 'border-box',
          }}
        >
          <Text role="sectionTitle">{title}</Text>
          <Box sx={{ marginLeft: 'auto' }}>{actions}</Box>
        </Box>
      </Box>
      <Box
        component="main"
        ref={mainRef}
        id="main-content"
        sx={{
          minHeight: 0,
          overflowY: scrollReady ? 'auto' : 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showBreadcrumbs ? (
          <Box
            sx={(theme) => ({
              borderBottom: `1px solid ${resolveSemanticColor(theme, 'border')}`,
              paddingX: breadcrumbPaddingX,
              paddingTop: s('lg'),
              paddingBottom: s('md'),
            })}
          >
            <PageBreadcrumbs />
          </Box>
        ) : null}
        <Box
          ref={contentRef}
          sx={{
            flex: hideFooter ? '0 0 auto' : '1 0 auto',
          }}
        >
          {children}
        </Box>
        {hideFooter ? null : (
          <Box
            component="footer"
            sx={(theme) => ({
              backgroundColor: resolveSemanticColor(theme, 'pageBg'),
              borderTop: `1px solid ${resolveSemanticColor(theme, 'border')}`,
              paddingX: shellPaddingX,
              paddingTop: { xs: s('xl'), md: s('2xl') },
              paddingBottom: { xs: s('xl'), md: s('2xl') },
              mt: pinFooterToViewport ? 'auto' : s('none'),
              opacity: footerReady ? 1 : 0,
              pointerEvents: footerReady ? 'auto' : 'none',
              visibility: footerReady ? 'visible' : 'hidden',
            })}
          >
            <Stack gap="lg" sx={{ maxWidth: '1180px', mx: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: footerGridTemplateColumns,
                  gap: { xs: s('md'), md: s('xl'), lg: s('2xl') },
                  alignItems: { xs: 'start', lg: 'stretch' },
                }}
              >
                <Stack
                  gap="sm"
                  sx={{
                    maxWidth: '420px',
                    height: { lg: '100%' },
                    justifyContent: { lg: 'space-between' },
                  }}
                >
                  <Stack gap="sm">
                    <Inline gap="sm" align="center" wrap="nowrap" sx={{ width: 'auto' }}>
                      <Box
                        component="img"
                        src="/logo.png"
                        alt=""
                        sx={{ height: '31px', width: '31px', display: 'block' }}
                      />
                      <Text
                        role="bodyStrong"
                        component="span"
                        transform="none"
                        sx={{
                          fontSize: '24px',
                          lineHeight: 1.2,
                        }}
                      >
                        FlowQA
                      </Text>
                    </Inline>
                    <Text role="bodyMuted" component="p" sx={footerTextSx}>
                      Test reporting, analytics and insights.
                    </Text>
                    <Inline gap="sm" align="center" wrap="nowrap" sx={{ width: 'auto' }}>
                      <EmailIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      {renderFooterLink({
                        href: 'mailto:support@flowqa.com',
                        label: 'support@flowqa.com',
                      })}
                    </Inline>
                  </Stack>
                  <Text role="body" color="textSecondary" sx={footerTextSx}>
                    © {new Date().getFullYear()} FlowQA Ltd
                  </Text>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      lg: `repeat(${String(footerGroups.length)}, minmax(max-content, 1fr))`,
                    },
                    gap: { xs: s('lg'), md: s('xl'), lg: s('2xl') },
                  }}
                >
                  {footerGroups.map((group, index) => (
                    <Stack
                      key={group.title}
                      gap="xs"
                      sx={(theme) => ({
                        borderLeft:
                          index > 0
                            ? {
                                xs:
                                  index % 2 === 1
                                    ? `1px solid ${resolveSemanticColor(theme, 'border')}`
                                    : undefined,
                                lg: `1px solid ${resolveSemanticColor(theme, 'border')}`,
                              }
                            : undefined,
                        pl:
                          index > 0
                            ? {
                                xs: index % 2 === 1 ? s('lg') : undefined,
                                lg: s('2xl'),
                              }
                            : undefined,
                      })}
                    >
                      <Stack gap="xs" sx={{ alignItems: 'flex-start', width: 'auto' }}>
                        {group.links.map((link) => (
                          <Box key={link.href}>{renderFooterLink(link)}</Box>
                        ))}
                      </Stack>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};
